import { Switch } from '@timelyapp/react';
import classNames from 'classnames';
import DurationFormat from 'components/DurationFormat';
import { Brain } from 'components/Icons';
import PrivacyIndicator from 'components/PrivacyIndicator';
import Tooltip from 'components/Tooltip';
import { DURATION_FORMAT } from 'constants/memory';
import React from 'react';
import { useSelector } from 'react-redux';
import { getCalendarProps } from 'redux/selectors/calendar';
import type { HourEditorProps } from 'redux/selectors/hours.draft';
import { getIsRouteCalendar } from 'redux/selectors/routing';
import type { Duration } from 'types/units';
import styles from './MemorySectionHeader.module.scss';

interface Props {
  duration: Duration;
  linkedMemoriesCount: number;
  onToggleMemories: () => void;
  ui: HourEditorProps['ui'];
  hasInstalledMemory: boolean;
  standalone?: boolean;
  style?: React.CSSProperties;
}

const MemorySectionHeader: React.FC<Props> = ({ duration, linkedMemoriesCount, ui, ...props }) => {
  const isCalendarRoute = useSelector(getIsRouteCalendar);
  const { ui: calendarUi } = useSelector(getCalendarProps);

  function renderMemoryIcon() {
    if (!ui || !isCalendarRoute || calendarUi.isSingleDay || calendarUi.multiUserMode) {
      return null;
    }

    return (
      <Tooltip title={ui.showMemories ? 'Hide Memories' : 'Show Memories'} position="left">
        <Switch
          checked={ui.showMemories}
          onChangeChecked={props.onToggleMemories}
          label={<Brain />}
          size="small"
        />
      </Tooltip>
    );
  }

  function renderHeaderText() {
    if (!props.hasInstalledMemory) {
      return <h1 className={styles.notConnected}>Memory is not connected</h1>;
    }

    if (!props.standalone && linkedMemoriesCount) {
      return (
        <>
          <PrivacyIndicator className={styles.privacy} />
          <h1>{linkedMemoriesCount} linked memories</h1>
        </>
      );
    }

    return (
      <>
        <PrivacyIndicator className={styles.privacy} />
        <h1 className={styles.notConnected}>No linked memories</h1>
      </>
    );
  }

  return (
    <section className={classNames(styles.container)} style={props.style || {}}>
      <div className={styles.details}>
        {linkedMemoriesCount ? (
          <>
            <div>
              <PrivacyIndicator className={styles.privacy} />
              <h1>{linkedMemoriesCount} linked memories</h1>
            </div>
            <DurationFormat
              className={styles.durationFormat}
              format={DURATION_FORMAT}
              value={duration.total_minutes}
            />
          </>
        ) : (
          <div>{renderHeaderText()}</div>
        )}
        {renderMemoryIcon()}
      </div>
      {ui.hasSelectedProject ? <div className={styles.border} /> : null}
    </section>
  );
};

export default MemorySectionHeader;
